/** 用于记录2000ms周期内的音频卡顿信息 */
export interface AudioStallInfo {
  stallCount: number, // 卡顿次数
  stallDuration: number, // 卡顿时长
  list: number[], // 卡顿列表, 仅用于打印
  extra: ExtraReportInfo,
}

export interface ExtraReportInfo {
  /** 详细信息list。100ms一次的统计，统计信息都存在这里 */
  stall_list: AudioStallMeta[];
  /** 2000ms周期内进行多少次统计 */
  stats_count: number;
}

/** 音频卡顿统计信息明细。默认每100ms执行一次 */
export interface AudioStallMeta {
  /** 100ms内丢弃的样本 */
  concealed?: number;
  /** 100ms内收到的样本 */
  received?: number;
  /** end - start */
  diff?: number;
  /** 上一次100ms统计结束的timestamp */
  start?: number;
  /** 100ms统计的结束timestamp */
  end?: number;
  /** getStats执行耗时 */
  get_stats_cost?: number;
  /** getStats执行开始时间 */
  get_stats_start?: number;
  /** getStats执行结束时间 */
  get_stats_end?: number;
  /** 记录一些原因 */
  reason?: string;
  /** 这次是否卡顿了？ */
  is_stall?: boolean;
}

class AudioStallObserver {
  // 音频统计间隔，默认为100ms
  static interval = 100

  // 音频卡顿阈值，默认为60%。即100ms中丢了60%的样本则视为卡顿
  static ratio = 0.6

  /** 周期性统计的timer */
  private _audioStallTimer?: number

  /** 记录上一次 getstats 的统计信息 */
  private _preSample?: {
    /** 时间戳 */
    ts: number;
    /** 忽略/丢弃的样本 */
    concealedSamples: number;
    /** 接收到的总样本数 */
    totalSamplesReceived: number;
  };

  /** 上一次report统计周期的末尾，是否存在卡顿。 */
  private _isStallInPreReportEnd = false

  private _stallList: AudioStallMeta[] = []

  private _audioTransceiver: RTCRtpTransceiver | null = null

  /** 开始统计卡顿率 */
  start (transceiver: RTCRtpTransceiver) {
    this.stop()
    this._audioTransceiver = transceiver
    // 如果间隔时间小于100ms，不进行统计
    if (AudioStallObserver.interval >= 100) {
      this._startStallCountInterval()
    }
  }

  /** 结束统计卡顿率 */
  stop () {
    this._stallList = []
    if (this._audioStallTimer) {
      clearTimeout(this._audioStallTimer)
      delete this._audioStallTimer
    }
  }

  destroy () {
    this.stop()
    this._audioTransceiver = null
  }

  /**
   * 获取周期内的音频卡顿信息
  */
  async getAudioStallInfo (): Promise<AudioStallInfo> {
    const extra = {
      stats_count: this._stallList.filter(v => v.get_stats_cost).length,
      stall_list: [...this._stallList]
    }
    // 上报前，最后一次计算卡顿，确保卡顿统计时间区间和上报时间区间一致
    try {
      await this._audioStallCount()
    } catch (e) {
      /** */
    }
    const reportInfo = computeStallInfo(this._stallList, 2, this._isStallInPreReportEnd)
    this._isStallInPreReportEnd = reportInfo.isStallInEnd
    // 重置下一个周期的卡顿统计
    this.stop()
    this._startStallCountInterval()
    return {
      stallCount: reportInfo.stall_count,
      stallDuration: reportInfo.stall_duration,
      list: reportInfo.list,
      extra
    }
  }

  /** 跑一个100ms的定时器，不停得获取getStats */
  _startStallCountInterval () {
    const loop = async () => {
      clearTimeout(this._audioStallTimer)
      delete this._audioStallTimer
      await this._audioStallCount()
      if (!this._audioStallTimer) {
        this._audioStallTimer = setTimeout(loop, AudioStallObserver.interval)
      }
    }
    this._audioStallTimer = setTimeout(loop, AudioStallObserver.interval)
  }

  /**
   * 计算周期内是否存在音频卡顿。
   * 该函数会周期性执行
   */
  async _audioStallCount () {
    // 过滤间隔时间过短的统计
    if (
      this._preSample &&
      Date.now() - this._preSample.ts < AudioStallObserver.interval * 0.5
    ) {
      return
    }

    const get_stats_start = Date.now()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let inboundRTP: any;
    let reason: string | undefined;
    let len = 0
    try {
      const reports = await this._audioTransceiver?.receiver.getStats()
      reports?.forEach(stats => {
        if (stats.type === 'inbound-rtp') {
          inboundRTP = stats
        }
        len++
      })
    } catch (e) {
      // @ts-expect-error 报错内容为unknown
      reason = e.message || JSON.stringify(e)
    }

    if (!this._preSample) {
      this._preSample = {
        ts: Date.now(),
        concealedSamples: inboundRTP?.concealedSamples || 0,
        totalSamplesReceived: inboundRTP?.totalSamplesReceived || 0
      }
      return
    }

    const get_stats_end = Date.now()
    const end = Date.now()
    const diff = end - this._preSample.ts
    if (!inboundRTP) {
      this._stallList.push({
        diff,
        start: this._preSample.ts,
        end,
        get_stats_cost: get_stats_end - get_stats_start,
        get_stats_start,
        get_stats_end,
        reason: !len ? 'no report' : reason || 'no inbound-rtp'
      })
    } else {
      const { concealedSamples, totalSamplesReceived } = inboundRTP
      const interval_concealed_samples =
        concealedSamples - this._preSample.concealedSamples
      const interval_samples_received =
        totalSamplesReceived - this._preSample.totalSamplesReceived
      // 当前周期是否存在卡顿？
      const is_stall =
        interval_concealed_samples >= 0 &&
        interval_samples_received >= 0 &&
        interval_concealed_samples >= AudioStallObserver.ratio * interval_samples_received
      // 存在卡顿
      this._stallList.push({
        concealed: interval_concealed_samples,
        received: interval_samples_received,
        diff,
        start: this._preSample.ts,
        end,
        get_stats_cost: get_stats_end - get_stats_start,
        get_stats_start,
        get_stats_end,
        is_stall
      })
      this._preSample.concealedSamples = concealedSamples
      this._preSample.totalSamplesReceived = totalSamplesReceived
      this._preSample.ts = end
    }
  }
}

/**
 * 音频卡顿计算
 * @param {AudioStallMeta[]} list 卡顿数据列表
 * @param {number} times  连续卡顿次数阈值。即几次连续卡顿就纳入卡顿周期
 * @param {boolean} isStallInPreEnd 前一次的卡顿周期是否存在卡顿
 * @return {*}
 */
const computeStallInfo = (list: AudioStallMeta[], times: number, isStallInPreEnd: boolean) => {
  const res = {
    stall_count: 0,
    stall_duration: 0,
    isStallInEnd: false,
    list: [] as number[]
  }
  /** 连续卡顿的次数 */
  let count = 0
  /** 连续卡顿时长 */
  let duration = 0

  for (let idx = 0; idx < list.length; idx++) {
    const info = list[idx]
    // 如果有卡顿，记录卡顿次数和时长
    if (info.is_stall) {
      count++
      duration += info.diff || 0
    }
    // 如果不存在卡顿，或者是最后一项。需要检查下已存在的连续卡顿是否需要上报/回调
    const lastItem = idx === list.length - 1
    if (!info.is_stall || lastItem) {
      // 是否需要上报？即卡顿次数已经足够上报或回调
      let needCount = count >= times

      // 统计周期内最后一项的index
      const rightIdx = idx + times - count - 1
      // 卡顿次数不够上报，需要检查卡顿样本/总样本 是否大于 卡顿阈值
      if (!needCount && count > 2) {
        let tempDuration = 0
        // （只有用户指定了卡顿阈值，才会走到这里面）
        list.slice(idx + 1, rightIdx + 1).forEach(v => {
          if (v.is_stall) {
            tempDuration += v.diff || 0
          }
        })
        needCount =
          duration + tempDuration >
          AudioStallObserver.interval * times * AudioStallObserver.ratio
        // 如果卡顿样本/总样本 > 卡顿阈值，那也将被纳入卡顿周期
        if (needCount) {
          idx = rightIdx
          duration += tempDuration
        }
      }
      if (needCount) {
        res.stall_count++
        res.stall_duration += duration
        res.list.push(duration)
      }
      if (lastItem) {
        res.isStallInEnd = needCount
      }

      // 重置连续卡顿统计
      count = duration = 0
    }
  }
  // 如果上一个周期也存在卡顿
  if (list[0]?.is_stall === true && isStallInPreEnd && res.stall_count > 0) {
    res.stall_count--
  }

  return res
}

export default AudioStallObserver